import React, { useState, useEffect, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { Button, Toolbar, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';

import ContentBox from '../layout/ContentBox';
import { convertToQid } from '../../utilities/stringHelper';
import ZerpaTable, { ColumnObject } from '../layout/table/ZerpaTable';
import useCheckbox from '../layout/table/useCheckbox';
import GenericDialog from '../GenericDialog';
import ComboBox, { DefaultDataType } from '../layout/ComboBox';
import UserEditDialog from './UserEditDialog';
import ZerpaSearch from '../layout/ZerpaSearch';
import ApiClient from '../../loopback/apiClient';
import {
  updateUsersRedux,
  showNotification,
} from '../../redux/actions';
import {
  UserManagementRow,
  QuipUser,
  RootState,
} from '../../redux/types';

type UserManagementType = {
  userTableRows?: UserManagementRow[];
  userTableColumns: ColumnObject[];
}

interface InstituteComboData extends DefaultDataType {
  label: string;
  value: number; // institute.id
}

const UserManagement: React.FC<UserManagementType> = ({
  userTableColumns,
  userTableRows,
}: UserManagementType) => {
  const zerpaSelect = useCheckbox();
  const dispatch = useDispatch();
  const userByIds = useSelector((state: RootState) => state.users.byId);
  const instituteByIds = useSelector((state: RootState) => state.institute.byId);
  const [buttonFlag, setButtonFlag] = useState<boolean>(false);
  const [showInstituteDialog, setShowInstituteDialog] = useState<boolean>(false);
  const [showUserEditDialog, setShowUserEditDialog] = useState<boolean>(false);
  const [selectedInstitutes, setSelectedInstitutes] = useState<InstituteComboData[]>([]);
  const [instituteList, setInstituteList] = useState<InstituteComboData[]>();
  const [searchText, setSearchText] = useState<string>('');
  const [filteredRows, setFilteredRows] = useState<UserManagementRow[] | undefined>(userTableRows);
  const locale = useSelector((state: RootState) => state.intl.locale);

  useEffect(() => {
    setButtonFlag(zerpaSelect.selectedIds.length > 0);
  }, [zerpaSelect.selectedIds]);

  useEffect(() => {
    /** Only runs if instituteList is empty */
    if (!instituteList && instituteByIds) {
      const instituteDatalist = Object.values(instituteByIds).map((inst) => ({
        label: `${inst.id} - ${inst.name}`,
        value: inst.id,
      } as InstituteComboData));
      setInstituteList(instituteDatalist);
    }
  }, [dispatch, showInstituteDialog, instituteByIds, instituteList]);

  useEffect(() => {
    if (!userTableRows) {
      return;
    }
    let filteredData = userTableRows;
    if (searchText) {
      const trimmedSearchText = searchText.toLowerCase().trim();
      filteredData = userTableRows.filter((val) => (
        val.userId.toLowerCase().includes(trimmedSearchText)
        || val.title?.toLowerCase().includes(trimmedSearchText)
        || val.firstname?.toLocaleLowerCase().includes(trimmedSearchText)
        || val.surname?.toLocaleLowerCase().includes(trimmedSearchText)
        || val.email.toLowerCase().includes(trimmedSearchText)
        || val.instituteName?.toLowerCase().includes(trimmedSearchText)
        || val.instituteCity?.toLowerCase().includes(trimmedSearchText)
        || val.instituteCountry?.toLowerCase().includes(trimmedSearchText)
        || val.instituteZip?.includes(trimmedSearchText)
        || val.instituteId?.toString().includes(trimmedSearchText)
      ));
    }
    setFilteredRows(filteredData);
  }, [searchText, userTableRows]);

  const showNotificationAction = useCallback((message: string | React.ReactElement) => (
    dispatch(showNotification(message))
  ), [dispatch]);

  const saveUserData = useCallback(async (updatedUser: QuipUser) => {
    dispatch(updateUsersRedux([updatedUser]));
  }, [dispatch]);

  const getSelectedUsers = (): QuipUser[] => {
    if (!userByIds) {
      return [];
    }
    return zerpaSelect.selectedIds.map((userId) => userByIds[userId as number]);
  };

  const onCloseInstituteDialog = (): void => {
    setShowInstituteDialog(false);
    setSelectedInstitutes([]);
  };

  const onSubmitInstituteDialog = async (): Promise<void> => {
    const selectedUsers = getSelectedUsers();
    if (!selectedUsers.length || selectedInstitutes.length <= 0) {
      return;
    }
    const { id: userId } = selectedUsers[0];
    const updatedUser = await ApiClient.updateUserInstitute(userId, selectedInstitutes[0].value);
    dispatch(updateUsersRedux([updatedUser]));
    setShowInstituteDialog(false);
    zerpaSelect.clearSelectedIds();
    setSelectedInstitutes([]);
  };

  const onSelectValue = (values: InstituteComboData[] | null) => {
    setSelectedInstitutes(values || []);
  };

  const onSearchTextChange = (searchTxt: string) => {
    setSearchText(searchTxt);
  };

  return (
    <>
      <Toolbar disableGutters>
        {
          buttonFlag
            ? (
              <>
                <div style={{ flexGrow: 1 }}>
                  <Button sx={{ mx: 0.5 }} variant="outlined" onClick={zerpaSelect.clearSelectedIds}><FormattedMessage id="resetPassword.buttonLabel" /></Button>
                </div>
                <Button sx={{ mx: 0.5 }} variant="outlined" onClick={() => setShowInstituteDialog(true)}><FormattedMessage id="userManagement.assignInstitute" /></Button>
                {
                  zerpaSelect.selectedIds.length === 1
                  && <Button sx={{ mx: 0.5 }} variant="outlined" onClick={() => setShowUserEditDialog(true)}><FormattedMessage id="edit" /></Button>
                }
              </>
            )
            : <Typography variant="h5" style={{ flexGrow: 1 }}><FormattedMessage id="instituteManagement.registeredUsers" /></Typography>
        }
      </Toolbar>
      <Toolbar disableGutters>
        <ZerpaSearch fullWidth onTextChange={onSearchTextChange} value={searchText} />
      </Toolbar>
      <div style={{ overflow: 'auto' }}>
        <ZerpaTable<UserManagementRow>
          keyIdentifier="id"
          tableRows={filteredRows}
          columns={userTableColumns}
          showPagination
          dense
          checkboxSelection={zerpaSelect}
        />
      </div>
      {/* change user's instituteId dialog */}
      <GenericDialog
        isOpen={showInstituteDialog}
        closeButtonLabel="close"
        onClose={onCloseInstituteDialog}
        maxWidth="sm"
        actions={[
          <Button sx={{ mx: 0.5 }} variant="contained" color="primary" key="confirm" onClick={onSubmitInstituteDialog} disabled={!selectedInstitutes.length}>
            <FormattedMessage id="confirm" />
          </Button>,
        ]}
      >
        <ContentBox title={<FormattedMessage id="userManagement.updateInstitute" />} elevation={0}>
          <div style={{ height: '280px' }}>
            <p>
              <FormattedMessage id="userManagement.updateInstituteForUser" />
              &nbsp;
              {
                zerpaSelect.selectedIds.length
                && userByIds
                && instituteByIds
                && (() => {
                  const selectedUsers = getSelectedUsers();
                  if (selectedUsers.length) {
                    const { id: userId, instituteId, userCountry } = selectedUsers[0];
                    return convertToQid(
                      userId,
                      instituteByIds[instituteId]?.country || userCountry,
                    );
                  }
                  return '';
                })()
              }
            </p>
            {
              instituteList
              && (
                <ComboBox<InstituteComboData>
                  keyId="institutes"
                  data={instituteList}
                  onChange={onSelectValue}
                  selectedItems={selectedInstitutes}
                  menuIsOpen
                  autoFocus
                  menuListHeight={200}
                />
              )
            }
          </div>
        </ContentBox>
      </GenericDialog>
      {
        userByIds
        && zerpaSelect.selectedIds.length > 0
        && (
          <UserEditDialog
            isOpen={showUserEditDialog}
            userData={userByIds[zerpaSelect.selectedIds[0] as number]}
            saveUserData={saveUserData}
            showNotification={showNotificationAction}
            onClose={(requiredRefresh?) => {
              if (requiredRefresh) {
                zerpaSelect.clearSelectedIds();
              }
              setShowUserEditDialog(false);
            }}
            locale={locale}
            lockCountry
          />
        )
      }
    </>
  );
};

export default UserManagement;
